import {AppActions, useAppContext} from "../../context/AppContext";
import {
    Button,
    Checkbox,
    Divider,
    Form,
    Input,
    InputNumber,
    notification,
    Popover,
    Select,
    Tabs,
    Typography
} from "antd";
import PopoverScrollContent from "../../components/PopoverScrollContent";
import SelectPlan from "../SelectPlan";
import ProjectInfo from "../ProjectInfo";
import SelectionInfo from "./SelectionInfo";
import React, {useEffect, useState} from "react";
import {CaretRightOutlined, WarningOutlined} from "@ant-design/icons";
import ApiService from "../../services/ApiService";
import {useUser} from "../../context/UserContext";
import ResultTable from "../../components/table/ResultTable";
import ResultHeader from "../../components/table/ResultHeader";
import ResultBody from "../../components/table/ResultBody";
import ResultRow from "../../components/table/ResultRow";
import ResultHeaderCell from "../../components/table/ResultHeaderCell";
import ResultBodyCell from "../../components/table/ResultBodyCell";
import HecRasHelper from "../../helpers/HecRasHelper";
import EmptyLine from "../outputs/EmptyLine";
import ResultText from "../outputs/ResultText";
import ResultView from "../../components/ResultView";
import ResultListBox from "../outputs/ui/ResultListBox";
import BridgeParameters from "../survey-automation/BridgeParameters";
import BridgeSurveyPoints from "../survey-automation/BridgeSurveyPoints";
import BridgePlot from "../survey-automation/BridgePlot";
import BridgeUI from "../survey-automation/BridgeUI";
import CrossSectionSurveyPoints from "../survey-automation/CrossSectionSurveyPoints";
import CrossSectionPlot from "../survey-automation/CrossSectionPlot";
import CrossSectionUI from "../survey-automation/CrossSectionUI";
import MapView from "../map/MapView";
import {v4} from "uuid";
import ProjectMD5 from "../../helpers/ProjectMD5";
import {useModal} from "../../context/ModalContext";
import SurveySaveAsPlan from "../survey-automation/SurveySaveAsPlan";
import {DEFAULT_PROJECTION, getProjectionOptions, loadProjectionText} from '../../helpers/ProjectionHelper';

export default function SurveyAutomationTab() {
    const type = 'base';
    const { user } = useUser()
    const {openModal, closeModal} = useModal()
    const {selectedHistoryId, getSelectedHistory, dispatchProject, getSelectedRS, mapContext, getProjectLayers, getRiverBoundingCoords, getMapData, selection, resetShapeFilters, getSelectedProject, getSelectedPlan, toolbarItem, appContext} = useAppContext()
    const [loading, setLoading] = useState(false)
    const [tab, setTab] = useState('')
    const [itemTab, setItemTab] = useState('str-points')
    const [selectedItem, setSelectedItem] = useState(null)
    const [projectInfo, setProjectInfo] = useState({});
    const [quickSelect, setQuickSelect] = useState('Quick Select')
    // Default projection for projects without .prj file
    const [defaultProjectionKey, setDefaultProjectionKey] = useState(
        user?.settings?.defaults?.spatial_projection || DEFAULT_PROJECTION
    );
    const [defaultProjectionText, setDefaultProjectionText] = useState(null);

    // Load default projection text when key changes
    useEffect(() => {
        const loadDefaultProjection = async () => {
            const text = await loadProjectionText(defaultProjectionKey);
            setDefaultProjectionText(text);
        };
        loadDefaultProjection();
    }, [defaultProjectionKey]);

    const getInputData = ({_selection}) => {
        // Use project projection if available, otherwise use default projection
        const hasProjectProjection = !!_selection?.project?.projection?.text;
        const projectionToUse = hasProjectProjection 
            ? _selection?.project?.projection 
            : (defaultProjectionText ? { text: defaultProjectionText, isDefault: true } : null);
        return {
            project: _selection.project.information,
            projection: projectionToUse,
            geometry: _selection.plan.geometry,
            flow: _selection.plan.flow,
            output: _selection.plan.mappedProfiles ?? [],
            plan: _selection.plan.plan,
            type: _selection.plan.type,
            profileNames: _selection.plan.mapProfiles.map((x) => {
                if (!x.targetName) x.targetName = x.target;

                return x;
            }),
            settings: user.settings,
            projectHash: _selection.projectHash,
            river: _selection.river?.river || null,
            reach: _selection.river?.reach || null,
            survey_geojson: _selection?.survey || null,
            params: {...getSurveyAutomationData().params}
        };
    };

    const getSurveyAutomationModifyList = (type) => {
        if(!type) {
            return getSurveyAutomationData().summary.data.filter(x => (x.status === 6 || x.status === 2))
        }

        return getSurveyAutomationData().summary.data.filter(x => x.type === type && (x.status === 6 || x.status === 2))
    }
    
    const getSurveyAutomationData = () => {
        const _prj = getSelectedProject(type)
        if(_prj?.surveyAutomationData) {
            return _prj?.surveyAutomationData
        }

        return {
            items: [],
            summary: {
                data: [],
                river: '',
                reach: '',
                map_data: {}
            },
            params: {},
        }
    }
    
    const setSurveyAutomationData = (data) => {
        const _prj = getSelectedProject(type)
        _prj.surveyAutomationData = {...data}

        dispatchProject({
            type: AppActions.SET_PROJECT,
            data: {
                project: _prj
            }
        })
    }

    const saveAsPlan = async () => {
        openModal({
            title: 'New Plan Information',
            style: {width: 500},
            component: (
                <SurveySaveAsPlan
                    onOk={async (planInfo) => {
                        closeModal()
                        setLoading(true)
                        try {
                            const filters = getSurveyAutomationData().summary.data.filter(x => x.apply)

                            const runKey = getSurveyAutomationData().summary?.run_key
                            if(!runKey)
                                return

                            const res = await ApiService.RasCheck.SurveyAutomationSaveAsPlan({
                                run_key: runKey,
                                filters
                            })

                            if(res.success) {
                                const data = JSON.parse(res.data)

                                const _prj = getSelectedProject(type)
                                const nextPlan = Math.max(..._prj.projectFiles.map(x => parseInt(x.plan.ext.replaceAll('p', ''))))+1
                                // const nextFlow = Math.max(..._prj.projectFiles.map(x => parseInt(x.flow.ext.replaceAll('f', ''))))+1
                                const nextGeo = Math.max(..._prj.projectFiles.map(x => parseInt(x.geometry.ext.replaceAll('g', ''))))+1

                                const _duplicate = JSON.parse(JSON.stringify(getSelectedPlan(type)))
                                delete _duplicate.LMA

                                _duplicate.id = v4()
                                _duplicate.isFindOutput = 'false'
                                _duplicate.plan.id = v4()
                                _duplicate.plan.ext = `p${nextPlan.toString().padStart(2, '0')}`
                                _duplicate.plan.fileName = `${_duplicate.plan.fileName.split('.')[0]}.${_duplicate.plan.ext}`
                                // _duplicate.plan.flowFileExt = `f${nextFlow.toString().padStart(2, '0')}`
                                _duplicate.plan.geometryFileExt = `g${nextGeo.toString().padStart(2, '0')}`
                                _duplicate.plan.title = planInfo.title
                                _duplicate.plan.text = data?.plan?.text
                                    .replace(/Geom File=\S+\s/g, `Geom File=${_duplicate.plan.geometryFileExt}\r`)
                                    .replace(/Flow File=\S+\s/g, `Flow File=${_duplicate.plan.flowFileExt}\r`)
                                    .replace(/Short Identifier=\S+\s/g, `Short Identifier=${planInfo.shortId}\r`)
                                    .replace(/Plan Title=\S+\s/g, `Plan Title=${planInfo.title}\r`)
                                _duplicate.plan.shortIdentifier = `Survey Model`
                                _duplicate.plan.fileName = `${_duplicate.plan.fileName.split('.')[0]}.${_duplicate.plan.ext}`

                                _duplicate.geometry.id = v4()
                                _duplicate.geometry.ext = _duplicate.plan.geometryFileExt
                                _duplicate.geometry.fileName = `${_duplicate.geometry.fileName.split('.')[0]}.${_duplicate.plan.geometryFileExt}`
                                _duplicate.geometry.text = data?.geometry?.text
                                    .replace(`Geom Title=${_duplicate.geometry.title.trim()}`, `Geom Title=${planInfo.title}`)
                                _duplicate.geometry.title = planInfo.title

                                // _duplicate.flow.id = v4()
                                // _duplicate.flow.ext = _duplicate.plan.flowFileExt
                                // _duplicate.flow.fileName = `${_duplicate.flow.fileName.split('.')[0]}.${_duplicate.plan.flowFileExt}`
                                // _duplicate.flow.text = data?.flow?.text
                                //     .replace(`Flow Title=${_duplicate.flow.title.trim()}`, `Flow Title=${planInfo.title}`)
                                // _duplicate.flow.title = planInfo.title
                                // _duplicate.flow.profiles = _duplicate.mapProfiles.map((x, i) => x.select)

                                _duplicate.projectHash = ProjectMD5({
                                    email: user.data.email,
                                    projectTitle: _prj.information.title,
                                    flowTitle: _duplicate.flow.title,
                                    flowFile: _duplicate.flow.fileName,
                                    geoTitle: _duplicate.geometry.title,
                                    geoFile: _duplicate.geometry.fileName,
                                    planTitle: _duplicate.plan.title,
                                    planFile: _duplicate.plan.fileName
                                })


                                const text = _prj.information.text.toString()
                                    .replace(/(Geom File=.+)/g, `$1\r\nGeom File=${_duplicate.geometry.ext}`)
                                    .replace(/(Plan File=.+)/g, `$1\r\nPlan File=${_duplicate.plan.ext}`)
                                    .replaceAll('\r\n', '\n')
                                    .replaceAll('\n', '\r\n')
                                _prj.information.text = text


                                _prj.projectFiles.push(_duplicate)
                                dispatchProject({
                                    type: AppActions.SET_PROJECT,
                                    data: {
                                        project: _prj
                                    }
                                })

                                notification.success({
                                    message: 'Survey Plan was added to project.'
                                })
                            }
                        }
                        catch (e) {
                            console.error(e)
                        }
                        setLoading(false)
                    }}
                />
            ),
            closable: true
        })
    }

    const runSurveyAutomation = async () => {
        try {
            setLoading(true)
            const _input = {...getInputData({_selection: selection.base})}
            setTab('')
            setSurveyAutomationData({
                items: [],
                summary: {
                    data: [],
                    river: '',
                    reach: '',
                    run_key: null
                }
            })

            const res = await ApiService.RasCheck.GenerateStructure(_input)

            if(res.success) {
                const _result = JSON.parse(res.data)
                const _summary = _result.summary

                _result.data = _result.data?.map(item => {
                    if(item?.type === 'Bridge') {
                        if(item?.deck_dist_data?.base?.station_table_us) {
                            item.deck_dist_data.base.station_table_us = item.deck_dist_data.base.station_table_us.map(r => [r[0]?.toFixed(2), r[1]?.toFixed(2), r[2]?.toFixed(2)])
                            item.deck_dist_data.base.station_table_ds = item.deck_dist_data.base.station_table_ds.map(r => [r[0]?.toFixed(2), r[1]?.toFixed(2), r[2]?.toFixed(2)])
                        }

                        if(item?.deck_dist_data?.base?.distance) {
                            item.deck_dist_data.base.distance = item.deck_dist_data.base.distance?.toFixed(2)
                            item.deck_dist_data.base.width = item.deck_dist_data.base.width?.toFixed(2)
                        }
                    }

                    return item
                })

                _summary?.data?.forEach(r => {
                    if(r.status === 1 || r.status === 4) {
                        r.apply = false
                    }
                    else {
                        r.apply = true
                    }
                })

                setSurveyAutomationData({
                    items: _result,
                    summary: _summary,
                    genState: 'generated'
                })
                setTab('Summary')
            }
        }
        catch (e) {
            console.error(e)
        }
        setLoading(false)
    }

    useEffect(() => {
        setProjectInfo({
            title: `${selection[type]?.project?.information?.title} (${selection[type]?.project?.information?.folderName})`,
            plan: `${selection[type]?.plan?.type} (.${selection[type]?.plan?.plan?.ext})`,
            river: selection[type]?.river?.river ? `${selection[type]?.river?.river} - ${selection[type]?.river?.reach}` : '',
        })
    }, [selectedItem]);

    // useEffect(() => {
    //     resetShapeFilters('automation')
    // }, []);

    return (
        <div>
            <Popover
                placement={'bottomLeft'}
                trigger={'click'}
                content={
                    <PopoverScrollContent maxHeight={600}>
                        <SelectPlan type={type}/>
                    </PopoverScrollContent>
                }
            >
                <SelectionInfo type={'base'} />
            </Popover>
            <br/>
            <ProjectInfo
                autoHeight={true}
                showNotes={true}
                folderName={selection?.base.project?.information?.folderName}
                title={selection?.base.project?.information?.title}
                descriptions={
                    selection?.base.project?.information?.descriptions
                }
                planTitle={selection?.base.plan?.plan?.title}
                version={
                    selection?.base.project?.projectFiles &&
                    selection.base.project?.projectFiles?.length > 0 &&
                    selection?.base.plan?.plan?.version
                }
            />
            <Divider/>

            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <Button
                    loading={loading}
                    size={'small'}
                    type={'primary'}
                    icon={<CaretRightOutlined/>}
                    onClick={runSurveyAutomation}
                >
                    Run Survey Automation
                </Button>
                {
                    !loading && (
                        <ResultListBox
                            suffixIcon={(<b>Select Cross Section</b>)}
                            value={tab}
                            list={['Summary', ...(getSurveyAutomationData().items?.data?.map(x => `${x.type} at ${x.cross_section} (${x.str_id})`) || [])]}
                            onChange={(val) => {
                                setTab(val)

                                const _item = getSurveyAutomationData()?.items?.data?.find(x => val === `${x.type} at ${x.cross_section} (${x.str_id})`)
                                if(_item) {
                                    setSelectedItem({
                                        ..._item
                                    })
                                    dispatchProject({
                                        type: AppActions.SET_SELECTED_RS,
                                        data: {rs: _item.cross_section}
                                    })

                                    if(_item.type === 'CrossSection' && itemTab === 'str-parameters') {
                                        setItemTab('str-points')
                                    }
                                }
                                else {
                                    setSelectedItem(null)
                                }
                            }}
                            width={400}
                            selectProps={{
                                showSearch: true
                            }}
                        />
                    )
                }
                { itemTab === 'str-map' &&  toolbarItem}
                {
                    getSurveyAutomationData().genState === 're-gen' && (
                        <Typography.Text>
                            Structure Params was updated. Please re-generate structures
                        </Typography.Text>
                    )
                }
                {
                    getSurveyAutomationData().genState === 'generated' && getSurveyAutomationData().summary?.run_key && !loading &&  (
                        <Button
                            size={'small'}
                            type={'primary'}
                            onClick={saveAsPlan}
                            disabled={loading}
                        >
                            Save As Plan
                        </Button>
                    )
                }
            </div>

            <div style={{marginTop: 16}} />
            {
                tab === 'Summary' && getSurveyAutomationData().summary.data?.length > 0 && (
                    <ResultView message_key={'SSA 01'}>
                        <ResultText>
                            In the survey data, the following {getSurveyAutomationModifyList('CrossSection').length} cross sections, {getSurveyAutomationModifyList('Bridge').length} bridge and {getSurveyAutomationModifyList('Culvert').length} culvert structures are identified along {getSurveyAutomationData().summary.river} - {getSurveyAutomationData().summary.reach}
                        </ResultText>

                        <EmptyLine />
                        <ResultTable style={{width: HecRasHelper.getWidth(107)}} data={getSurveyAutomationModifyList()?.map(r => ({csName: `${r.cross_section}${r.type !== 'CrossSection' ? ` (${r.type})` : ''}`, csNameVal: r.cross_section}))}>
                            <ResultHeader>
                                <ResultRow>
                                    <ResultHeaderCell style={{width: HecRasHelper.getWidth(10), textAlign: 'start'}}>RS</ResultHeaderCell>
                                    <ResultHeaderCell style={{width: HecRasHelper.getWidth(12), textAlign: 'start'}}>Type</ResultHeaderCell>
                                    <ResultHeaderCell style={{width: HecRasHelper.getWidth(18), textAlign: 'start'}}>Survey Structure ID</ResultHeaderCell>
                                    <ResultHeaderCell style={{width: HecRasHelper.getWidth(10), textAlign: 'start'}}>US RS</ResultHeaderCell>
                                    <ResultHeaderCell style={{width: HecRasHelper.getWidth(10), textAlign: 'start'}}>DS RS</ResultHeaderCell>
                                    <ResultHeaderCell style={{width: HecRasHelper.getWidth(30), textAlign: 'start'}}>Notes</ResultHeaderCell>
                                    <ResultHeaderCell style={{width: HecRasHelper.getWidth(17), textAlign: 'start'}}>
                                        <Select
                                            style={{minWidth: HecRasHelper.getWidth(15)}}
                                            size={'small'}
                                            value={quickSelect}
                                            onChange={(val) => {
                                                setQuickSelect(val)
                                                const automationData = getSurveyAutomationData()
                                                if(val === 'all') {
                                                    automationData.summary.data.forEach((x) => {
                                                        if(x.status === 1 || x.status === 4) {
                                                            x.apply = false
                                                            return
                                                        }
                                                        x.apply = true
                                                    })
                                                }
                                                else if(val === 'xs') {
                                                    automationData.summary.data.forEach((x) => {
                                                        if(x.type === 'CrossSection' && !(x.status === 1 || x.status === 4)) {
                                                            x.apply = true
                                                            return
                                                        }
                                                        x.apply = false
                                                    })
                                                }
                                                else if(val === 'structure') {
                                                    automationData.summary.data.forEach((x) => {
                                                        if(x.type === 'CrossSection' || x.status === 1 || x.status === 4) {
                                                            x.apply = false
                                                            return
                                                        }
                                                        x.apply = true
                                                    })
                                                }

                                                setSurveyAutomationData(automationData)
                                            }}
                                        >
                                            <Select.Option value={'all'}>All</Select.Option>
                                            <Select.Option value={'xs'}>XS</Select.Option>
                                            <Select.Option value={'structure'}>Structure</Select.Option>
                                        </Select>
                                    </ResultHeaderCell>
                                </ResultRow>
                            </ResultHeader>
                            <ResultBody>
                                {
                                    getSurveyAutomationData().summary.data.map(r => (
                                        <ResultRow>
                                            <ResultBodyCell style={{width: HecRasHelper.getWidth(10)}}>{r.cross_section_text}</ResultBodyCell>
                                            <ResultBodyCell style={{width: HecRasHelper.getWidth(12)}}>{r.type}</ResultBodyCell>
                                            <ResultBodyCell style={{width: HecRasHelper.getWidth(18)}}>{r.str_id ?? '-'}</ResultBodyCell>
                                            <ResultBodyCell style={{width: HecRasHelper.getWidth(10)}}>{r.us_cs ?? '-'}</ResultBodyCell>
                                            <ResultBodyCell style={{width: HecRasHelper.getWidth(10)}}>{r.ds_cs ?? '-'}</ResultBodyCell>
                                            <ResultBodyCell style={{width: HecRasHelper.getWidth(30)}}>
                                                {r.status === 1 && (<ResultText style={{color: 'orange'}}>Exist in the model {r.status_details?.length > 0 ? '(' + r.status_details?.join(', ') + ')' : ''}</ResultText>)}
                                                {r.status === 2 && (<ResultText style={{color: 'green'}}>Generated {r.status_details?.length > 0 ? '(' + r.status_details?.join(', ') + ')' : ''}</ResultText>)}
                                                {r.status === 3 && (<ResultText style={{color: 'red'}}>Couldn’t generate {r.status_details?.length > 0 ? '(' + r.status_details?.join(', ') + ')' : ''}</ResultText>)}
                                                {r.status === 4 && (<ResultText style={{color: 'orange'}}>Exist in the model (14)</ResultText>)}
                                                {r.status === 5 && (<ResultText style={{color: 'black'}}>No Modify {r.status_details?.length > 0 ? '(' + r.status_details?.join(', ') + ')' : ''}</ResultText>)}
                                                {r.status === 6 && (<ResultText style={{color: 'green'}}>Burned XS {r.status_details?.length > 0 ? '(' + r.status_details?.join(', ') + ')' : ''}</ResultText>)}
                                                {r.status === 7 && (<ResultText style={{color: 'red'}}>Couldn't Burned XS {r.status_details?.length > 0 ? '(' + r.status_details?.join(', ') + ')' : ''}</ResultText>)}
                                            </ResultBodyCell>
                                            <ResultBodyCell>
                                                <Checkbox disabled={r.status === 4 || r.status === 1} checked={r.apply} onChange={(e) => {
                                                    const automationData = getSurveyAutomationData()
                                                    automationData.summary.data.forEach((x) => {
                                                        if(x.cross_section_text === r.cross_section_text) {
                                                            r.apply = e.target.checked
                                                        }
                                                    })
                                                    setSurveyAutomationData(automationData)
                                                }}/>
                                            </ResultBodyCell>
                                        </ResultRow>
                                    ))
                                }
                            </ResultBody>
                        </ResultTable>

                        {
                            (getSurveyAutomationData().summary?.data?.map(r => r?.status_details?.length).reduce((a,b) => a+b) > 0 || getSurveyAutomationData().summary?.data?.filter(r => r?.status === 4)?.length > 0) && (
                                <ResultText style={{marginTop: 16}}>
                                    <ResultTable>
                                        <ResultHeader>
                                            <ResultRow>
                                                <ResultHeaderCell style={{width: HecRasHelper.getWidth(15), textAlign: 'start'}}>Code</ResultHeaderCell>
                                                <ResultHeaderCell style={{width: HecRasHelper.getWidth(48), textAlign: 'start'}}>Message</ResultHeaderCell>
                                            </ResultRow>
                                        </ResultHeader>
                                        <ResultBody>
                                            <ResultRow>
                                                <ResultBodyCell>0</ResultBodyCell>
                                                <ResultBodyCell>Unknown error.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>1</ResultBodyCell>
                                                <ResultBodyCell>Missing LCUSL points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>2</ResultBodyCell>
                                                <ResultBodyCell>Missing LCUSR points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>3</ResultBodyCell>
                                                <ResultBodyCell>Missing LCDSL points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>4</ResultBodyCell>
                                                <ResultBodyCell>Missing LCDSR points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>5</ResultBodyCell>
                                                <ResultBodyCell>Missing TODUSL points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>6</ResultBodyCell>
                                                <ResultBodyCell>Missing TODUSR points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>7</ResultBodyCell>
                                                <ResultBodyCell>Missing TODDSL points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>8</ResultBodyCell>
                                                <ResultBodyCell>Missing TODDSR points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>9</ResultBodyCell>
                                                <ResultBodyCell>Missing RDCL points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>10</ResultBodyCell>
                                                <ResultBodyCell>Upstream left stations can't detect from RDCL points.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>11</ResultBodyCell>
                                                <ResultBodyCell>Upstream right stations can't detect from RDCL points.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>12</ResultBodyCell>
                                                <ResultBodyCell>Downstream left stations can't detect from RDCL points.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>13</ResultBodyCell>
                                                <ResultBodyCell>Downstream right stations can't detect from RDCL points.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>14</ResultBodyCell>
                                                <ResultBodyCell>There is no survey point for this structure</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>15</ResultBodyCell>
                                                <ResultBodyCell>Missing TOBL points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>16</ResultBodyCell>
                                                <ResultBodyCell>Missing TOBR points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>17</ResultBodyCell>
                                                <ResultBodyCell>Missing TOSL points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>18</ResultBodyCell>
                                                <ResultBodyCell>Missing TOSR points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>19</ResultBodyCell>
                                                <ResultBodyCell>Missing INVUS and INVDS points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                            <ResultRow>
                                                <ResultBodyCell>20</ResultBodyCell>
                                                <ResultBodyCell>Missing GR,CH,TOS points in survey data.</ResultBodyCell>
                                            </ResultRow>
                                        </ResultBody>
                                    </ResultTable>
                                </ResultText>
                            )
                        }
                    </ResultView>
                )
            }
            {
                tab === 'Summary' && getSurveyAutomationData().summary.data?.length === 0 && (
                    <>
                        Please Run Survey Automation
                    </>
                )
            }
            {
                tab !== 'Summary' && selectedItem && !loading && (
                    <Tabs
                        style={{minHeight: window.innerHeight - 250}}
                        size={'small'}
                        type={'card'}
                        activeKey={itemTab || 'str-parameters'}
                        onChange={(tab) => {
                            setItemTab(tab || 'str-parameters')
                            if(tab === 'str-map') {
                                const interval = setInterval(() => {
                                    const div = document.getElementById('footer-div')
                                    if(div) {
                                        div.scrollIntoView({ behavior: 'smooth', block: 'end' });
                                        clearInterval(interval)
                                    }
                                }, 200)
                            }
                        }}
                    >
                        <Tabs.TabPane tab={'Survey Points'} key={'str-points'}>
                            {
                                selectedItem.type === 'Bridge' && (
                                    <BridgeSurveyPoints
                                        br={selectedItem}
                                        survCodeFilters={getSurveyAutomationData()?.bridgeSurvCodeFilters || []}
                                        onSurvCodeFilter={(prop, values) => {
                                            const _data = {...getSurveyAutomationData()}
                                            _data.bridgeSurvCodeFilters = values
                                            setSurveyAutomationData(_data)
                                        }}
                                    />
                                )
                            }
                            {
                                selectedItem.type === 'CrossSection' && (
                                    <CrossSectionSurveyPoints
                                        item={selectedItem}
                                        survCodeFilters={getSurveyAutomationData()?.xsSurvCodeFilters || []}
                                        onSurvCodeFilter={(prop, values) => {
                                            const _data = {...getSurveyAutomationData()}
                                            _data.xsSurvCodeFilters = values
                                            setSurveyAutomationData(_data)
                                        }}
                                    />
                                )
                            }
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={'Plot'} key={'str-plot'}>
                            {
                                selectedItem.type === 'Bridge' && (
                                    <BridgePlot
                                        br={selectedItem}
                                    />
                                )
                            }
                            {
                                selectedItem.type === 'CrossSection' && (
                                    <CrossSectionPlot
                                        item={selectedItem}
                                    />
                                )
                            }
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={'UI'} key={'str-ui'}>
                            {
                                selectedItem.type === 'Bridge' && (
                                    <BridgeUI
                                        br={selectedItem}
                                    />
                                )
                            }
                            {
                                selectedItem.type === 'CrossSection' && (
                                    <CrossSectionUI
                                        item={selectedItem}
                                    />
                                )
                            }
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={'Map'} key={'str-map'}>
                            <MapView
                                type={type}
                                projection={selection[type]?.project?.projection?.text}
                                projectInfo={projectInfo}
                                updateKey={selection[type]?.project}
                                mapData={getSurveyAutomationData()?.summary?.map_data}
                                projectLayers={getProjectLayers(type, selection)}
                                riverBoundingCoords={getRiverBoundingCoords(type, getSurveyAutomationData()?.summary?.map_data)}
                                selectedRS={getSelectedRS()}
                                mapSize={{height: window.innerHeight - 300, width: window.innerWidth-125}}
                                highlightSurvey={selectedItem?.dict?.burned_point_list || (selectedItem?.dict?.point_list || [])}
                                onProjectLayerChange={(shapes) => {
                                    const _project = appContext?.projects?.find(x => x.id === selection[type]?.project?.id)
                                    if (_project) {
                                        _project.shapes = [...shapes]
                                        dispatchProject({
                                            type: AppActions.SET_PROJECT,
                                            data: {
                                                project: {
                                                    ..._project
                                                },
                                                map: {
                                                    type,
                                                    [type]: {
                                                        update: v4()
                                                    }
                                                }
                                            }
                                        })
                                    }
                                }}
                            />
                            <div id={'map-bottom'} />
                        </Tabs.TabPane>
                        {
                            selectedItem?.type !== 'CrossSection' && (
                                <Tabs.TabPane tab={'Structure Parameters'} key={'str-parameters'}>
                                    {
                                        selectedItem?.type === 'Bridge' && (
                                            <BridgeParameters
                                                br={selectedItem}
                                                params={getSurveyAutomationData()?.params && getSurveyAutomationData()?.params[selectedItem?.dict?.str_id]}
                                                onValuesChange={(data) => {
                                                    setSurveyAutomationData({
                                                        ...getSurveyAutomationData(),
                                                        params: {
                                                            ...getSurveyAutomationData().params,
                                                            [selectedItem?.dict?.str_id]: data
                                                        },
                                                        genState: 're-gen'
                                                    })
                                                }}
                                            />
                                        )
                                    }
                                </Tabs.TabPane>
                            )
                        }
                    </Tabs>
                )
            }
        </div>
    )
}